import React from "react";
import {
	Box,
	Divider,
	Flex,
	FormControl,
	Icon,
	Input,
	Select,
	Switch,
	Text,
	VStack,
} from "@chakra-ui/react";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import {
	Label,
	commonInputProps,
	commonSelectProps,
	sectionCardProps,
} from "../matrimonyForm.ui";
import {
	DOSHAM_OPTIONS,
	NAKSHATRA_OPTIONS,
	RASI_OPTIONS,
} from "src/utils/matrimonyOptions";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";

type Props = {
	formData: MatrimonyFormData;
	errors: FormErrors;
	updateField: (field: keyof MatrimonyFormData, value: string) => void;
};

const Step5HoroscopeDetails: React.FC<Props> = ({ formData, errors, updateField }) => {
	const disabled = formData.horoUnknown === "true";

	return (
		<VStack align="stretch" spacing={{ base: 4, md: 6 }}>
			<Box {...sectionCardProps}>
				<Flex align="flex-start" gap={3}>
					<Box
						w="40px"
						h="40px"
						borderRadius="12px"
						bg="rgba(74,44,42,0.08)"
						display="flex"
						alignItems="center"
						justifyContent="center"
						flexShrink={0}
					>
						<Icon viewBox="0 0 24 24" color="#4A2C2A" boxSize={5}>
							<path
								fill="currentColor"
								d="M12 2a1 1 0 0 1 1 1v1.07a7.002 7.002 0 0 1 5.93 5.93H20a1 1 0 1 1 0 2h-1.07A7.002 7.002 0 0 1 13 19.93V21a1 1 0 1 1-2 0v-1.07A7.002 7.002 0 0 1 5.07 14H4a1 1 0 1 1 0-2h1.07A7.002 7.002 0 0 1 11 4.07V3a1 1 0 0 1 1-1Zm0 4a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z"
							/>
						</Icon>
					</Box>
					<Box flex="1">
						<Text fontSize="lg" fontWeight="700" color="#4A2C2A">
							Horoscope Details
						</Text>
						<Text mt={1} fontSize="sm" color="#6C757D">
							Share your horoscope details to help find better matches.
						</Text>
					</Box>
				</Flex>

				<Divider mt={4} borderColor="rgba(108,117,125,0.18)" />

				<Flex
					mt={4}
					align={{ base: "flex-start", md: "center" }}
					justify="space-between"
					gap={3}
					flexDir={{ base: "column", md: "row" }}
				>
					<Text fontSize="sm" color="#6C757D" fontWeight="500">
						I don&apos;t know my horoscope details
					</Text>
					<Switch
						isChecked={formData.horoUnknown === "true"}
						onChange={(e) =>
							updateField("horoUnknown", e.target.checked ? "true" : "false")
						}
					/>
				</Flex>

				<Box display="none">
					<Input
						value={String(formData.horoUnknown)}
						onChange={() => {
							/* noop */
						}}
					/>
				</Box>

				<Flex gap={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
					<FormControl flex="1">
						<Label>{FIELD_LABELS.NAKSHATRA.toUpperCase()}</Label>
						<Select
							{...commonSelectProps}
							isDisabled={disabled}
							value={formData.nakshatra}
							onChange={(event) =>
								updateField("nakshatra", event.target.value)
							}
							placeholder="Select Nakshatra"
						>
							{NAKSHATRA_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormControl>

					<FormControl flex="1">
						<Label>{FIELD_LABELS.RASI.toUpperCase()}</Label>
						<Select
							{...commonSelectProps}
							isDisabled={disabled}
							value={formData.rasi}
							onChange={(event) => updateField("rasi", event.target.value)}
							placeholder="Select Rasi"
						>
							{RASI_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormControl>
				</Flex>

				<Flex gap={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
					<FormControl flex="1">
						<Label>{FIELD_LABELS.GOTHRAM.toUpperCase()}</Label>
						<Input
							{...commonInputProps}
							isDisabled={disabled}
							value={formData.gothram}
							onChange={(event) =>
								updateField("gothram", event.target.value)
							}
							placeholder="Ex: Kashyapa"
						/>
					</FormControl>

					<FormControl flex="1" isInvalid={Boolean(errors.dosham)}>
						<Label>{FIELD_LABELS.DOSHAM.toUpperCase()}</Label>
						<Select
							{...commonSelectProps}
							isDisabled={disabled}
							value={formData.dosham}
							onChange={(event) =>
								updateField("dosham", event.target.value)
							}
							placeholder="Select dosham"
						>
							{DOSHAM_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
						{errors.dosham && !disabled ? (
							<Text mt={1} fontSize="xs" color="#E53E3E">
								{errors.dosham}
							</Text>
						) : null}
					</FormControl>
				</Flex>
			</Box>
		</VStack>
	);
};

export default Step5HoroscopeDetails;
